import type { VolumeProfileSession } from './state/types';
import type { VolumeProfileWindow } from './window';
import { KIASI_PRICE_SCALE } from 'src/lib/kiasi';
import type { KiasiAdjustmentMode, KiasiVolumeProfile } from 'src/lib/kiasi';
import type { VolumeBarSnapshot, VolumePriceLevel } from 'src/lib/kiasi/bars';

/** One histogram level in display units (dollars, shares). */
export type VolumeProfileLevel = VolumePriceLevel;

/** A volume profile in display units, independent of where the bars came from. */
export interface VolumeProfileSnapshot {
    /** Ascending price order. */
    readonly levels: readonly VolumeProfileLevel[];
    readonly barCount: number;
    readonly totalVolume: number;
    readonly bidVolume: number;
    readonly askVolume: number;
    readonly midVolume: number;
    readonly premarketVolume: number;
    readonly regularVolume: number;
    readonly postmarketVolume: number;
    /** Ask volume minus bid volume. */
    readonly delta: number;
    readonly vwapVolume: number;
    /** Sum of price times size over VWAP-eligible prints, in dollar-shares. */
    readonly vwapNotional: number;
    /** `null` when no VWAP-eligible volume traded. */
    readonly vwap: number | null;
    readonly poc: number;
    readonly vah: number;
    readonly val: number;
    readonly adjust: KiasiAdjustmentMode;
}

export const EMPTY_VOLUME_PROFILE_LEVELS: readonly VolumeProfileLevel[] = Object.freeze([]);

export function toDollars(scaledPrice: number): number {
    return scaledPrice / KIASI_PRICE_SCALE;
}

export function fromKiasiProfile(profile: KiasiVolumeProfile): VolumeProfileSnapshot {
    return {
        levels: profile.levels.map((level) => ({
            price: toDollars(level.price),
            bid: level.bid,
            ask: level.ask,
            mid: level.mid,
            premarket: level.pre,
            regular: level.reg,
            postmarket: level.post,
            total: level.total,
        })),
        barCount: profile.bar_count,
        totalVolume: profile.total_vol,
        bidVolume: profile.bid_vol,
        askVolume: profile.ask_vol,
        midVolume: profile.mid_vol,
        premarketVolume: profile.pre_vol,
        regularVolume: profile.reg_vol,
        postmarketVolume: profile.post_vol,
        delta: profile.delta,
        vwapVolume: profile.vwap_volume,
        vwapNotional: toDollars(profile.vwap_notional),
        vwap: profile.vwap === null ? null : toDollars(profile.vwap),
        poc: toDollars(profile.poc),
        vah: toDollars(profile.vah),
        val: toDollars(profile.val),
        adjust: profile.adjust,
    };
}

export function sessionVolume(level: VolumeProfileLevel, session: VolumeProfileSession): number {
    switch (session) {
        case 'all':
            return level.total;
        case 'premarket':
            return level.premarket;
        case 'regular':
            return level.regular;
        case 'postmarket':
            return level.postmarket;
        default: {
            const _exhaustiveCheck: never = session;
            return 0;
        }
    }
}

/**
 * Restricts levels to one session. Kiasi splits a level by aggressor side and
 * by session independently, so the side split of a single session is not on
 * the wire; it is estimated by scaling the level's side split to the session's
 * share of its volume.
 */
export function filterSession(
    levels: readonly VolumeProfileLevel[],
    session: VolumeProfileSession,
): readonly VolumeProfileLevel[] {
    if (session === 'all') return levels;
    return levels.map((level) => {
        const total = sessionVolume(level, session);
        const scale = level.total === 0 ? 0 : total / level.total;
        return {
            price: level.price,
            bid: level.bid * scale,
            ask: level.ask * scale,
            mid: level.mid * scale,
            premarket: session === 'premarket' ? level.premarket : 0,
            regular: session === 'regular' ? level.regular : 0,
            postmarket: session === 'postmarket' ? level.postmarket : 0,
            total,
        };
    });
}

/** Price increments, in dollars, a user can bucket the histogram by. */
export const VOLUME_PROFILE_INCREMENTS = [0.01, 0.05, 0.1, 0.25, 0.5, 1, 2.5, 5, 10] as const;
export type VolumeProfileIncrementValue = (typeof VOLUME_PROFILE_INCREMENTS)[number];
export type VolumeProfileIncrement = 'auto' | VolumeProfileIncrementValue;

export const DEFAULT_VOLUME_PROFILE_TARGET_ROWS = 40;

/** Smallest listed increment that keeps the histogram at or under `targetRows` rows. */
export function autoIncrement(
    levels: readonly VolumeProfileLevel[],
    targetRows: number = DEFAULT_VOLUME_PROFILE_TARGET_ROWS,
): VolumeProfileIncrementValue {
    if (levels.length < 2) return VOLUME_PROFILE_INCREMENTS[0];
    // Integer cents: a dollar difference such as 207.5 - 207.1 is not exactly 0.4.
    const spanCents = toCents(levels[levels.length - 1].price) - toCents(levels[0].price);
    const rawCents = spanCents / targetRows;
    return (
        VOLUME_PROFILE_INCREMENTS.find((increment) => toCents(increment) >= rawCents) ??
        VOLUME_PROFILE_INCREMENTS.at(-1)!
    );
}

function toCents(price: number): number {
    return Math.round(price * 100);
}

/**
 * Merges levels onto an increment grid by flooring each price in Kiasi's
 * 1/10,000-dollar integer units, so sub-cent prices land in the bucket below
 * them and floating-point drift cannot move a level. Increments under a cent
 * are treated as a cent. Input and output are ascending.
 */
export function bucketLevels(
    levels: readonly VolumeProfileLevel[],
    incrementDollars: number,
): readonly VolumeProfileLevel[] {
    const incrementScaled = Math.max(KIASI_PRICE_SCALE / 100, Math.round(incrementDollars * KIASI_PRICE_SCALE));
    const buckets = new Map<number, VolumeProfileLevel>();
    for (const level of levels) {
        const key = Math.floor(Math.round(level.price * KIASI_PRICE_SCALE) / incrementScaled) * incrementScaled;
        const current = buckets.get(key);
        buckets.set(
            key,
            current === undefined
                ? { ...level, price: key / KIASI_PRICE_SCALE }
                : {
                      price: current.price,
                      bid: current.bid + level.bid,
                      ask: current.ask + level.ask,
                      mid: current.mid + level.mid,
                      premarket: current.premarket + level.premarket,
                      regular: current.regular + level.regular,
                      postmarket: current.postmarket + level.postmarket,
                      total: current.total + level.total,
                  },
        );
    }
    return [...buckets.values()];
}

export interface VolumeProfileKeyLevels {
    readonly pocIndex: number;
    readonly vahIndex: number;
    readonly valIndex: number;
}

/**
 * Point of control and 70% value area over ascending levels, following
 * Kiasi's rule (`kiasi-domain/src/bar/value_area.rs`): expand from the POC
 * one neighbor at a time toward the larger volume, ties toward the lower
 * price. Returns -1 indexes for an empty or zero-volume histogram.
 */
export function keyLevels(levels: readonly VolumeProfileLevel[]): VolumeProfileKeyLevels {
    let pocIndex = -1;
    let totalVolume = 0;
    for (let index = 0; index < levels.length; index++) {
        totalVolume += levels[index].total;
        if (pocIndex === -1 || levels[index].total > levels[pocIndex].total) pocIndex = index;
    }
    if (pocIndex === -1 || totalVolume === 0) return { pocIndex: -1, vahIndex: -1, valIndex: -1 };

    const target = totalVolume * 0.7;
    let included = levels[pocIndex].total;
    let low = pocIndex;
    let high = pocIndex;
    while (included < target && (low > 0 || high + 1 < levels.length)) {
        const lower = low > 0 ? levels[low - 1].total : null;
        const upper = high + 1 < levels.length ? levels[high + 1].total : null;
        const chooseLower = upper === null || (lower !== null && lower >= upper);
        if (chooseLower) {
            low -= 1;
            included += levels[low].total;
        } else {
            high += 1;
            included += levels[high].total;
        }
    }
    return { pocIndex, vahIndex: high, valIndex: low };
}

/** Whether a live bar belongs in the profile: still open, and opened inside the window. */
export function liveBarInWindow(bar: VolumeBarSnapshot | null, window: VolumeProfileWindow): bar is VolumeBarSnapshot {
    return bar !== null && !bar.closed && bar.openTimeMs >= window.fromMs && bar.openTimeMs <= window.toMs;
}

/**
 * Adds the live open bar to a history profile. Closed bars are never merged:
 * history serves them once they close, and the model refetches on every close,
 * so a closed live bar would be counted twice. POC and value area are
 * recomputed over the merged cent levels with the same rule Kiasi uses.
 */
export function mergeOpenBar(
    snapshot: VolumeProfileSnapshot,
    bar: VolumeBarSnapshot | null,
    window: VolumeProfileWindow,
): VolumeProfileSnapshot {
    if (!liveBarInWindow(bar, window)) return snapshot;
    // Keyed by the exact scaled price: Kiasi keeps four-decimal levels below $1.00, so keying by
    // cents would collapse distinct levels and drop volume.
    const byPrice = new Map<number, VolumeProfileLevel>();
    const add = (level: VolumeProfileLevel): void => {
        const key = Math.round(level.price * KIASI_PRICE_SCALE);
        const current = byPrice.get(key);
        byPrice.set(
            key,
            current === undefined
                ? level
                : {
                      price: current.price,
                      bid: current.bid + level.bid,
                      ask: current.ask + level.ask,
                      mid: current.mid + level.mid,
                      premarket: current.premarket + level.premarket,
                      regular: current.regular + level.regular,
                      postmarket: current.postmarket + level.postmarket,
                      total: current.total + level.total,
                  },
        );
    };
    for (const level of snapshot.levels) add(level);
    for (const level of bar.levels) add(level);
    const levels = [...byPrice.entries()].sort(([a], [b]) => a - b).map(([, level]) => level);
    const { pocIndex, vahIndex, valIndex } = keyLevels(levels);
    const vwapVolume = snapshot.vwapVolume + bar.vwapVolume;
    const vwapNotional = snapshot.vwapNotional + bar.vwapNotional;
    return {
        levels,
        barCount: snapshot.barCount + 1,
        totalVolume: snapshot.totalVolume + bar.totalVolume,
        bidVolume: snapshot.bidVolume + bar.bidVolume,
        askVolume: snapshot.askVolume + bar.askVolume,
        midVolume: snapshot.midVolume + bar.midVolume,
        premarketVolume: snapshot.premarketVolume + bar.premarketVolume,
        regularVolume: snapshot.regularVolume + bar.regularVolume,
        postmarketVolume: snapshot.postmarketVolume + bar.postmarketVolume,
        delta: snapshot.delta + bar.delta,
        vwapVolume,
        vwapNotional,
        vwap: vwapVolume > 0 ? vwapNotional / vwapVolume : null,
        poc: pocIndex === -1 ? snapshot.poc : levels[pocIndex].price,
        vah: vahIndex === -1 ? snapshot.vah : levels[vahIndex].price,
        val: valIndex === -1 ? snapshot.val : levels[valIndex].price,
        adjust: snapshot.adjust,
    };
}
